import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import adminService from '../../../services/adminService';
import notificationService from '../../../services/notificationService';
import Icon from '../../common/Icons';
import { ActivityFeed, NotificationPanel } from '../dashboard';
import { AdminPersonnelPageHeader } from '../ui';
import { AP_PATHS } from '../adminPersonnelPaths';
import { AP_COPY } from '../adminPersonnelCopy';
import { mapNotificationFromApi } from '../utils/notificationMappers';
import { mapActivityFromApi } from '../utils/activityMappers';
import '../AdminPersonnelPortal.css';

const DEFAULT_STATS = {
  totalCompanies: 0,
  activeCompanies: 0,
  totalMembers: 0,
  activeMembers: 0,
  totalAgents: 0,
  totalConjoints: 0,
  totalEnfants: 0,
  pendingRequests: 0
};

function DashStatCard({ title, value, icon, variant, subtitle, loading, onClick }) {
  return (
    <button
      type="button"
      className={`ap-stat-card ap-stat-card--${variant}`}
      onClick={onClick}
      disabled={loading}
      aria-busy={loading}
    >
      <div className="ap-stat-card__head">
        <span className="ap-stat-card__title">{title}</span>
        <span className="ap-stat-card__icon" aria-hidden>
          <Icon name={icon} size={18} />
        </span>
      </div>
      <div className="ap-stat-card__value">
        {loading ? (
          <span className="ap-stat-card__skeleton" aria-label="Chargement" />
        ) : (
          value?.toLocaleString('fr-FR') ?? 0
        )}
      </div>
      {subtitle && !loading && <div className="ap-stat-card__sub">{subtitle}</div>}
    </button>
  );
}

function DashQuickAction({ title, description, icon, onClick }) {
  return (
    <button type="button" className="ap-quick-card ap-quick-card--accent" onClick={onClick}>
      <span className="ap-quick-card__icon" aria-hidden>
        <Icon name={icon} size={22} />
      </span>
      <span>
        <div className="ap-quick-card__title">{title}</div>
        <div className="ap-quick-card__hint">{description}</div>
      </span>
    </button>
  );
}

export default function AdminPersonnelDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(DEFAULT_STATS);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);

  const loadDashboardStats = useCallback(async (signal) => {
    try {
      setStatsLoading(true);
      const response = await adminService.getDashboardStats({ signal });
      if (response?.success && response.data) {
        setStats({ ...DEFAULT_STATS, ...response.data });
      } else {
        setStats(DEFAULT_STATS);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Erreur lors du chargement des statistiques:', error);
      setStats(DEFAULT_STATS);
    } finally {
      if (!signal?.aborted) setStatsLoading(false);
    }
  }, []);

  const loadRecentActivities = useCallback(async (signal) => {
    try {
      setActivitiesLoading(true);
      const response = await adminService.getRecentActivities({ limit: 10 }, { signal });
      const rows = response?.data || [];
      setActivities(rows.map(mapActivityFromApi));
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Erreur lors du chargement des activités récentes:', error);
      setActivities([]);
    } finally {
      if (!signal?.aborted) setActivitiesLoading(false);
    }
  }, []);

  const loadNotifications = useCallback(async (signal) => {
    try {
      setNotificationsLoading(true);
      const response = await notificationService.list({ limit: 5 }, { signal });
      const rows = response?.data || [];
      setNotifications(rows.map(mapNotificationFromApi));
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Erreur lors du chargement des notifications:', error);
      setNotifications([]);
    } finally {
      if (!signal?.aborted) setNotificationsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    const controller = new AbortController();
    loadDashboardStats(controller.signal);
    loadRecentActivities(controller.signal);
    loadNotifications(controller.signal);
    return () => controller.abort();
  }, [loadDashboardStats, loadRecentActivities, loadNotifications]);

  useEffect(() => {
    const cleanup = refreshAll();
    return cleanup;
  }, [refreshAll]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (error) {
      console.error('Erreur lors du marquage de la notification:', error);
    }
  };

  const { DASHBOARD: D } = AP_COPY;
  const welcomeName =
    user?.first_name || user?.last_name
      ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
      : null;

  const quickActions = [
    {
      id: 'add-company',
      title: D.QUICK_NEW_COMPANY_TITLE,
      description: D.QUICK_NEW_COMPANY_DESC,
      icon: 'building',
      onClick: () => navigate(AP_PATHS.COMPANIES)
    },
    {
      id: 'add-user',
      title: D.QUICK_NEW_MEMBER_TITLE,
      description: D.QUICK_NEW_MEMBER_DESC,
      icon: 'user',
      onClick: () => navigate(AP_PATHS.MEMBERS)
    },
    {
      id: 'pricing',
      title: D.QUICK_PRICING_TITLE,
      description: D.QUICK_PRICING_DESC,
      icon: 'chartBar',
      onClick: () => navigate(AP_PATHS.PRICING)
    },
    {
      id: 'invoices',
      title: D.QUICK_INVOICES_TITLE,
      description: D.QUICK_INVOICES_DESC,
      icon: 'clipboard-list',
      onClick: () => navigate(AP_PATHS.INVOICES)
    },
    {
      id: 'activities',
      title: D.QUICK_ACTIVITIES_TITLE,
      description: D.QUICK_ACTIVITIES_DESC,
      icon: 'history',
      onClick: () => navigate(AP_PATHS.ACTIVITIES)
    },
    {
      id: 'notifications',
      title: D.QUICK_NOTIFICATIONS_TITLE,
      description: D.QUICK_NOTIFICATIONS_DESC,
      icon: 'bell',
      onClick: () => navigate(AP_PATHS.NOTIFICATIONS)
    }
  ];

  return (
    <div className="ap-dash">
      <AdminPersonnelPageHeader
        badge={D.BADGE}
        title={welcomeName ? `Bienvenue, ${welcomeName}` : D.TITLE}
        description={D.WELCOME_SUB}
        actions={
          <Button
            variant="outline-primary"
            className="rounded-3"
            onClick={refreshAll}
            disabled={statsLoading}
          >
            {D.REFRESH}
          </Button>
        }
      />

      <div className="ap-stats-grid mb-4">
        <DashStatCard
          title={D.STAT_COMPANIES}
          value={stats.totalCompanies}
          icon="building"
          variant="indigo"
          loading={statsLoading}
          subtitle={D.STAT_COMPANIES_SUB(stats.activeCompanies, stats.totalCompanies)}
          onClick={() => navigate(AP_PATHS.COMPANIES)}
        />
        <DashStatCard
          title={D.STAT_MEMBERS}
          value={stats.totalMembers}
          icon="users"
          variant="emerald"
          loading={statsLoading}
          subtitle={D.STAT_MEMBERS_SUB(stats.activeMembers, stats.totalMembers)}
          onClick={() => navigate(AP_PATHS.MEMBERS)}
        />
        <DashStatCard
          title={D.STAT_AGENTS}
          value={stats.totalAgents}
          icon="user"
          variant="teal"
          loading={statsLoading}
          subtitle={D.STAT_AGENTS_SUB}
          onClick={() => navigate(AP_PATHS.MEMBERS)}
        />
        <DashStatCard
          title={D.STAT_DEPENDANTS}
          value={stats.totalConjoints + stats.totalEnfants}
          icon="users"
          variant="amber"
          loading={statsLoading}
          subtitle={D.STAT_DEPENDANTS_SUB(stats.totalConjoints, stats.totalEnfants)}
          onClick={() => navigate(AP_PATHS.MEMBERS)}
        />
      </div>

      <div className="ap-panel mb-4">
        <div className="ap-panel__head ap-panel__head--bar">{D.SECTION_QUICK}</div>
        <div className="ap-panel__body">
          <Row className="g-3">
            {quickActions.map((action) => (
              <Col key={action.id} xs={12} sm={6} xl={4}>
                <DashQuickAction {...action} />
              </Col>
            ))}
          </Row>
        </div>
      </div>

      <Row className="g-3">
        <Col lg={8}>
          <div className="ap-panel h-100">
            <ActivityFeed
              activities={activities}
              loading={activitiesLoading}
              onViewAll={() => navigate(AP_PATHS.ACTIVITIES)}
            />
          </div>
        </Col>
        <Col lg={4}>
          <div className="ap-panel h-100">
            <NotificationPanel
              notifications={notifications}
              loading={notificationsLoading}
              onMarkAsRead={handleMarkAsRead}
              onViewAll={() => navigate(AP_PATHS.NOTIFICATIONS)}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
}
